import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  catchAsync,
  CI_PROJECT_CATEGORY_TYPE_OPTIONS,
  convertToCamelCase,
  IS_SUCCESS_RESPONSE,
  parseFiltersDataIntoQuery,
  PROJECT_TYPES,
  RS_PROJECT_CATEGORY_TYPE_OPTIONS,
  STATES_OPTIONS,
} from "src/helpers";
import FilterFormComponent from "src/modules/FilterModule/FilterFormComponent";
import NewFilterLayout from "./NewFilterLayout";
import { getAllStatesRequestApi } from "src/services/requests/Common";
import { fetchProjectStautusDropdownApi } from "src/services/requests/Projects";
import { useQueryRequest } from "src/pages/Marketplace/core/QueryRequestProvider";

type Props = {
  onChange: (e: any) => void;
  setopen?: (val: boolean) => void;
  open?: boolean;
};

const FILTERS = [
  {
    name: "Project Staus",
    dataKey: "projectStatus",
    type: "dropdown",
    options: [],
    btnWidth: 100,
  },
  {
    name: "Buy/Sell",
    dataKey: "orderType",
    type: "dropdown",
    // optionsType: "multiselect",
    options: ["Buy", "Sell", "Lease"].map((d: any) => ({ label: convertToCamelCase(d), value: d })),
    btnWidth: 100,
  },
  {
    name: "Select Services",
    dataKey: "customerType",
    type: "dropdown",
    options: [
      {
        label: "Residential",
        value: "Residential",
      },
      {
        label: "Societies",
        value: "Society",
      },
      {
        label: "Commercial",
        value: "Commercial",
      },
      {
        label: "Industrial",
        value: "Industrial",
      },
    ],
    btnWidth: 200,
  },

  {
    name: "Select Products",
    dataKey: "projectCategoryType",
    type: "list",
    optionsType: "multiselect",
    conditionalKey: "customerType",
    conditionalValue: "Commercial",
    options: CI_PROJECT_CATEGORY_TYPE_OPTIONS,
  },
  {
    name: "Select Products",
    dataKey: "projectCategoryType",
    type: "list",
    optionsType: "multiselect",
    conditionalKey: "customerType",
    conditionalValue: "Residential",
    options: RS_PROJECT_CATEGORY_TYPE_OPTIONS,
  },
  {
    name: "Project Type",
    dataKey: "projectType",
    type: "list",
    optionsType: "multiselect",
    options: PROJECT_TYPES,
  },
  {
    name: "State",
    dataKey: "state",
    type: "dropdown",
    options: STATES_OPTIONS,
  },
  {
    name: "Project ID",
    dataKey: "projectId",
    type: "text",
  },
  {
    name: "Project Size",
    dataKey: "projectSizeInKW",
    type: "range",
    min: 0,
    max: 100000,
    steps: 1000,
    suffix: "kW",
    unit: "kW",
  },
  {
    name: "Project Size",
    dataKey: "projectSizeInSqFt",
    type: "range",
    min: 0,
    max: 100000,
    steps: 1000,
    suffix: "acres",
    unit: "acres",
  },
];

const RESET_FILTER = {
  projectCategoryType: [],
  customerType: "",
  buySell: "",
  projectType: [],
  investments: {
    min: 0,
    max: 0,
  },
  state: "",
  project_id: "",
  projectName: "",
  projectSizeInKW: {
    min: 0,
    max: 0,
  },
};

const FilterMain = ({ onChange, open, setopen }: Props) => {
  const { state, updateState } = useQueryRequest();
  const { search } = useLocation();
  const [states, setstates] = useState([]);
  const [status, setstatus] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedFilters, setselectedFilters] = useState<{
    projectCategoryType: string[];
    customerType: string;
    projectType: string[];
    buySell: string;
    investments: {
      min: number;
      max: number;
    };
    state: string;
    project_id: string;
    projectName: string;
    projectSizeInKW: {
      min: number;
      max: number;
    };
  }>(RESET_FILTER);

  useEffect(() => {
    setselectedFilters({
      ...RESET_FILTER,
      customerType: "Commercial",
    });
    handleFetchStates();
    handleFetchStatus();
    return () => {};
  }, []);

  const handleFetchStates = catchAsync(
    async () => {
      setIsLoading(true);
      return await getAllStatesRequestApi();
    },
    (result: any) => {
      if (IS_SUCCESS_RESPONSE(result)) {
        setstates(result?.data?.map((d: any) => ({ label: d, value: d })));
      }
      setIsLoading(false);
    },
    () => {
      setIsLoading(false);
    }
  );

  const handleFetchStatus = catchAsync(
    async () => {
      setIsLoading(true);
      return await fetchProjectStautusDropdownApi();
    },
    (result: any) => {
      if (IS_SUCCESS_RESPONSE(result)) {
        setstatus(result?.data?.map((d: any) => ({ label: d, value: d })));
      }
      setIsLoading(false);
    },
    () => {
      setIsLoading(false);
    }
  );

  useEffect(() => {
    if (search) {
      let filterData = search
        ? search
            .replace("?", "")
            .split("&")
            .reduce((acc: any, curr: any) => {
              const [key, value] = curr.split("=");
              if (["projectCategoryType", "residentialAndSocieties", "projectType", "buySell"].includes(key) && value) {
                acc[key as keyof object] = value ? value.split(",").map((d: any) => decodeURI(d)) : [];
              } else if (["investments"].includes(key) && value) {
                let [min, max] = value.split(":");
                acc[key as keyof object] = value
                  ? {
                      min: min,
                      max: max,
                    }
                  : {
                      min: 0,
                      max: 0,
                    };
              } else acc[key as keyof object] = value ? decodeURI(value) : "";
              return acc;
            }, {})
        : {};

      setselectedFilters({ ...RESET_FILTER, ...filterData });
      onChange(parseFiltersDataIntoQuery(filterData));
    }
    return () => {};
  }, [search]);

  const _FILTERS = useMemo(() => {
    const items: any = [...FILTERS];

    const itemIndex = items.findIndex((d: any) => d.dataKey === "state");
    const itemIndex_status = items.findIndex((d: any) => d.dataKey === "projectStatus");

    if (itemIndex !== -1 && Array.isArray(states) && states.length > 0) {
      items[itemIndex] = {
        ...items[itemIndex],
        options: states,
      };
    }

    if (itemIndex_status !== -1 && Array.isArray(status) && status.length > 0) {
      items[itemIndex_status] = {
        ...items[itemIndex_status],
        options: status,
      };
    }
    const type = state.filter?.projectCategoryType || "";

    const typeArray = type.split(",").map((val: any) => val.trim());

    if (typeArray.includes("Land for RE Projects") && typeArray.length > 1) {
      return items;
    } else if (typeArray.length === 1 && typeArray[0] === "Land for RE Projects") {
      return items.filter((cur: any) => cur?.dataKey !== "projectSizeInKW");
    } else {
      return items.filter((cur: any) => cur?.dataKey !== "projectSizeInSqFt");
    }
  }, [states, FILTERS, status, state]);

  return (
    <>
      <div className="lg:hidden">
        <FilterFormComponent
          filterData={_FILTERS}
          selectedFilters={selectedFilters}
          onChange={(e: any) => {
            onChange(e);
          }}
          open={open}
          setopen={setopen}
        />
      </div>
      <div className="flex justify-center px-4">
        <NewFilterLayout
          filterData={_FILTERS}
          selectedFilters={selectedFilters}
          onChange={(e: any) => {
            onChange(e);
          }}
          open={open}
          setopen={setopen}
        />
      </div>
    </>
  );
};

export default FilterMain;
